import { SplitFactory, useSplitTreatments } from "@splitsoftware/splitio-react";
import React from "react";
import { useLocation } from "react-router-dom";

import { PageLoader } from "../components";
import { useAuth0 } from "./auth0";
import { useConfig } from "./config";

interface SplitIOProps {
  children: JSX.Element | null;
}

export function SplitIOProvider(props: SplitIOProps) {
  const { children } = props;
  const config = useConfig();
  if (!config.splitioApiKey) {
    return <>{children}</>;
  }

  const sdkConfig: SplitIO.IBrowserSettings = {
    core: {
      authorizationKey: config.splitioApiKey,
      key: "generic",
    },
  };

  return (
    <SplitFactory config={sdkConfig}>
      {({ isReady }) => (isReady ? children : <PageLoader />)}
    </SplitFactory>
  );
}

// "on" satisfies both ways a flag is read here: `=== "on"` for opt-in flags and
// `!== "off"` for the default-on ones.
const SUPERADMIN_TREATMENT: SplitIO.TreatmentWithConfig = {
  treatment: "on",
  config: null,
};

interface FlagReadOptions {
  // Read the flag as the partner's own users do, even for a superadmin. Only
  // for a flag whose "on" has no permissive side: one that withholds something
  // broken, picks one of two looks, or retires a page in favour of another.
  // Forcing such a flag on would hide or misrepresent what the partner has.
  asPartner?: boolean;
}

// The `?FLAG=on|off` override, remembered for the rest of the tab. The URL is
// read before the remembered value, so `?FLAG=off` can undo an earlier
// `?FLAG=on` in the same tab.
function readFlagOverride(
  name: string,
  search: string,
  treatValue = "on"
): boolean | undefined {
  const flagOnUrl = new URLSearchParams(search).get(name);
  if (flagOnUrl) {
    sessionStorage.setItem(name, `${treatValue === flagOnUrl}`);
    return flagOnUrl === treatValue;
  }

  const remembered = sessionStorage.getItem(name);
  if (remembered) return remembered !== "false";
  return undefined;
}

// Every flag is read through this hook. Superadmins support every partner, so a
// flag switched off for the partner they are helping must not hide from them
// the page they were asked to fix; overriding here covers flags added later
// without touching each one. `?FLAG=off` hands one flag back to Split, so a
// superadmin can still see the page the partner sees; nothing in the URL can
// turn a flag on here. Only the Dashboard's sign-in carries a superadmin, so
// customer-facing pages read Split as before, and a session that has ended no
// longer counts, so the sign-in page's switches keep working.
function useTreatments(
  names: string[],
  attributes?: SplitIO.Attributes,
  { asPartner = false }: FlagReadOptions = {}
): SplitIO.TreatmentsWithConfig {
  const { treatments } = useSplitTreatments({ names, attributes });
  const { isAuthenticated, userProfile } = useAuth0();
  const { search } = useLocation();

  if (asPartner || !isAuthenticated || !userProfile?.isSuperAdmin) {
    return treatments;
  }
  return Object.fromEntries(
    names.map((name) => [
      name,
      readFlagOverride(name, search) === false
        ? treatments[name]
        : SUPERADMIN_TREATMENT,
    ])
  );
}

function useFeatureFlag(
  name: string,
  partnerId: string,
  treatValue = "on",
  rules?: { [key: string]: string }
) {
  const treatments = useTreatments([name], {
    partner_id: partnerId,
    ...rules,
  });
  const { search } = useLocation();

  return (
    readFlagOverride(name, search, treatValue) ??
    treatments[name].treatment === treatValue
  );
}

export function useDailyLocationGalleryFeatureFlag() {
  const treatments = useTreatments(["CH_6344_DAILY_LOCATION_GALLERY"]);

  return treatments["CH_6344_DAILY_LOCATION_GALLERY"].treatment === "on";
}

export function useMultiLocationGalleryFeatureFlag() {
  const treatments = useTreatments(["CH_6345_MULTI_LOCATION_GALLERY"]);

  return treatments["CH_6345_MULTI_LOCATION_GALLERY"].treatment === "on";
}

export function useWeworkPostPurchaseFeatureFlag() {
  const treatments = useTreatments(["CH_24736_WEWORK_POST_PURCHASE"]);

  return treatments["CH_24736_WEWORK_POST_PURCHASE"].treatment === "on";
}

// Permanent feature flags

export function useFMMembershipChangePlanFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag("FM_MEMBERSHIP_CHANGE_PLAN", partnerId, "on", {
    user_id: userId,
  });
}

export function useFMCampaignArchiveFlag(partnerId: string, userId: string) {
  return useFeatureFlag("FM_CAMPAIGN_ARCHIVE", partnerId, "on", {
    user_id: userId,
  });
}

export function useFMCancelAccessBookingFF(partnerId: string) {
  const treatments = useTreatments(["FM_CANCEL_ACCESS_BOOKING"], {
    partner_id: partnerId,
  });

  return treatments["FM_CANCEL_ACCESS_BOOKING"].treatment === "on";
}

export function useFMDisplayingNavBarTabCalendar(partnerId: string) {
  const treatments = useTreatments(["FM_DISPLAYING_NAV_BAR_TAB_CALENDAR"], {
    partner_id: partnerId,
  });

  return treatments["FM_DISPLAYING_NAV_BAR_TAB_CALENDAR"].treatment === "on";
}

export function useFMDisplayingNavBarTabInventory(partnerId: string) {
  const treatments = useTreatments(["FM_DISPLAYING_NAV_BAR_TAB_INVENTORY"], {
    partner_id: partnerId,
  });

  return treatments["FM_DISPLAYING_NAV_BAR_TAB_INVENTORY"].treatment === "on";
}

export function useFMInventoryWithWindow(partnerId: string) {
  const treatments = useTreatments(["FM_INVENTORY_WITH_WINDOW"], {
    partner_id: partnerId,
  });

  return treatments["FM_INVENTORY_WITH_WINDOW"].treatment === "on";
}

export function useRequestToBookPaymentAuth(partnerId: string) {
  return useFeatureFlag("CH_47441_REQUEST_TO_BOOK_PAYMENT_AUTH", partnerId);
}

export function useMailchimpDataViewDashboardFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH_49007_MAILCHIMP_DATA_VIEW_DASHBOARD", partnerId);
}

export function useKlaviyoDataViewDashboardFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH_49075_KLAVIYO_DATA_VIEW_DASHBOARD", partnerId);
}

export function useGohighlevelDataViewDashboardFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag(
    "CH_48951_GOHIGHLEVEL_DATA_VIEW_DASHBOARD",
    partnerId,
    "on",
    { user_id: userId }
  );
}

export function useMembershipImagesSelfServeFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH_49032_MEMBERSHIP_IMAGES_SELF_SERVE", partnerId);
}

export function useFaqSelfServeFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH_48912_FAQ_SELF_SERVE", partnerId);
}

export function useFMSelfServeMembershipStartDateFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag(
    "FM_SELF_SERVE_MEMBERSHIP_START_DATE",
    partnerId,
    "on",
    { user_id: userId }
  );
}

/**
 * Sends unauthenticated Dashboard visitors to our own sign-in page instead of
 * Auth0's hosted one — on every host, so being signed out always looks the
 * same. Evaluated before anyone signs in, so it takes no partner or user
 * attributes — it is a global on/off, and turning it off restores the hosted
 * page without a deploy.
 */
export function useEmbeddedLoginFeatureFlag() {
  const treatments = useTreatments(["FM_EMBEDDED_LOGIN"]);

  return treatments["FM_EMBEDDED_LOGIN"].treatment === "on";
}

/**
 * Has "Forgot password?" ask our backend for the email, which leads to the
 * Dashboard's own set-password page, instead of Auth0. The backend reads the
 * same flag for the Team page's invite and reset emails, so it is a global
 * on/off like the embedded sign-in flag: this is read before anyone signs in,
 * so nothing could be targeted, and one switch means no one gets our email from
 * one place and Auth0's from the other. Off restores Auth0's emails without a
 * deploy — and a link we already sent keeps working, because the page itself
 * is never gated.
 */
export function useDashboardPasswordPageFeatureFlag() {
  const treatments = useTreatments(["FM_DASHBOARD_PASSWORD_PAGE"]);

  return treatments["FM_DASHBOARD_PASSWORD_PAGE"].treatment === "on";
}

/**
 * Shows Continue with SSO on the Dashboard sign-in page. Global, like the
 * password-page flag: it is read before anyone signs in, so there is no partner
 * or user to target. Only the button is gated — the Sign in with SSO page that
 * invite emails open, and the backend's enforcement, work whatever it says.
 * `?FM_SSO_LOGIN=on` shows the button in one tab while the flag is off.
 */
export function useSsoLoginFeatureFlag() {
  return useFeatureFlag("FM_SSO_LOGIN", "");
}

// Gates the General information section of the "Offering Content" hub. The backend
// deliberately carries no matching gate — its write endpoints rely on authentication plus
// offering ownership — so this is the only switch that hides the feature.
export function useOfferingContentSelfServeFeatureFlag(partnerId: string) {
  return useFeatureFlag("FM_SELF_SERVE_OFFERING_CONTENT", partnerId);
}

// Gates the Promo box section of the same hub. Deliberately its own flag rather than
// reusing the one above: promo would otherwise go live wherever that flag is already on,
// with no way to pilot it separately. Each section of the hub is enabled independently,
// which is why the hub renders a tab bar only when more than one section is on and its
// nav entry appears when any section is.
export function usePromoBoxSelfServeFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag("FM_SELF_SERVE_OFFERING_PROMO_BOX", partnerId, "on", {
    user_id: userId,
  });
}

// Gates the Name & description section of the same hub. Its own flag for the same reason promo
// box has one: sharing another section's flag would switch this on wherever that one already
// pilots, with no way to pilot it separately.
export function useOfferingNameDescriptionSelfServeFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag(
    "FM_SELF_SERVE_OFFERING_NAME_DESCRIPTION",
    partnerId,
    "on",
    { user_id: userId }
  );
}

export function useFMSelfServeLocationEditFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag("FM_SELF_SERVE_LOCATION_EDIT", partnerId, "on", {
    user_id: userId,
  });
}

export function useFMSelfServeBookableHoursFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag("FM_SELF_SERVE_BOOKABLE_HOURS", partnerId, "on", {
    user_id: userId,
  });
}

export function useFMSelfServeBookingPoliciesFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag("FM_SELF_SERVE_BOOKING_POLICIES", partnerId, "on", {
    user_id: userId,
  });
}

// Gates the staff order-confirmation editor — its hub row and the whole drill-down beneath it.
// Its own flag rather than the notifications hub's: the hub already ships, and this is the first
// email a partner can point at their own team, so it pilots on its own.
export function useFMSelfServeStaffOrderConfirmationFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag(
    "FM_SELF_SERVE_STAFF_ORDER_CONFIRMATION",
    partnerId,
    "on",
    { user_id: userId }
  );
}

// Gates the guest order-confirmation editor — its hub row and the whole drill-down beneath it.
// Its own flag per the one-pilot-per-email precedent: this email lands in paying customers'
// inboxes, so it rolls out on its own terms. The backend checks the same key.
export function useFMSelfServeGuestOrderConfirmationFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag(
    "FM_SELF_SERVE_GUEST_ORDER_CONFIRMATION",
    partnerId,
    "on",
    { user_id: userId }
  );
}

export function useFMBookingReminderEmailFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag("FM_BOOKING_REMINDER_EMAIL", partnerId, "on", {
    user_id: userId,
  });
}

export function useFMSettingsNotificationsFeatureFlag(partnerId: string) {
  const treatments = useTreatments(["FM_SETTINGS_NOTIFICATIONS"], {
    partner_id: partnerId,
  });

  return treatments["FM_SETTINGS_NOTIFICATIONS"].treatment === "on";
}

export function useLayoutReorderFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH_49168_LAYOUT_REORDER", partnerId);
}

// The partner-level switches behind the permissions a partner admin grants
// (src/fm/hooks/use_can.ts): whether the partner has the capability at all.
// Who holds it is the user's permission, not these flags.
export interface PermissionEntitlements {
  createOrders: boolean;
  cancelOrders: boolean;
  manageCatalog: boolean;
}

export function useFMPermissionEntitlements(
  partnerId: string,
  userId: string
): PermissionEntitlements {
  const treatments = useTreatments(
    [
      "FM_SETTING_NEW_ORDER",
      "FM_ORDER_CANCELLATION",
      "CH20084_DISPLAY_NAV_SPACE_EDITING",
    ],
    { partner_id: partnerId, user_id: userId }
  );

  return {
    createOrders: treatments["FM_SETTING_NEW_ORDER"].treatment === "on",
    cancelOrders: treatments["FM_ORDER_CANCELLATION"].treatment === "on",
    manageCatalog:
      treatments["CH20084_DISPLAY_NAV_SPACE_EDITING"].treatment === "on",
  };
}

export function useFMBookingCancellationFeatureFlag(
  partnerId: string,
  userId: string
) {
  const treatments = useTreatments(["FM_BOOKING_CANCELLATION"], {
    partner_id: partnerId,
    user_id: userId,
  });

  return treatments["FM_BOOKING_CANCELLATION"].treatment === "on";
}

interface FMDownloadReceiptFlagArgs {
  partnerId: string;
  userId: string;
  // ISO-2 country of the location the receipt is for. Lets the flag suppress
  // receipts per jurisdiction rather than per partner, which matters because a
  // partner can operate in several countries at once.
  locationCountry?: string | null;
}

export function useFMDownloadReceiptFeatureFlag({
  partnerId,
  userId,
  locationCountry,
}: FMDownloadReceiptFlagArgs) {
  const treatments = useTreatments(
    ["FM_DOWNLOAD_RECEIPT"],
    {
      partner_id: partnerId,
      user_id: userId,
      // Split's list matcher is case-sensitive; normalize so a lowercase value
      // from any payload still matches a rule written in ISO-2 form.
      ...(locationCountry
        ? { location_country: locationCountry.toUpperCase() }
        : {}),
    },
    // "off" withholds a receipt we know renders wrong in that jurisdiction, so
    // a superadmin must not be handed it either.
    { asPartner: true }
  );

  // Default ON: hidden only when explicitly opted out ("off").
  return treatments["FM_DOWNLOAD_RECEIPT"].treatment !== "off";
}

export function useModifyBookingDateTimeFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag("FM_MODIFY_BOOKING_DATE_TIME", partnerId, "on", {
    user_id: userId,
  });
}

export function useFMOrderLocationChangeFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag("FM_ORDER_LOCATION_CHANGE", partnerId, "on", {
    user_id: userId,
  });
}

// Widens the room picker on an existing order to rooms of a compatible but
// different space type. Gates the widened rooms request rather than any JSX:
// with the flag off nothing is fetched and the picker keeps its current
// same-type list, so the whole feature is inert.
export function useFMOrderSpaceTypeChangeFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag("FM_ORDER_SPACE_TYPE_CHANGE", partnerId, "on", {
    user_id: userId,
  });
}

// Turns Guests on a daily day-pass order into a control for the number of
// passes. The backend has no flag of its own, so this is the whole switch.
export function useFMOrderQuantityChangeFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag("FM_ORDER_QUANTITY_CHANGE", partnerId, "on", {
    user_id: userId,
  });
}

export function useFMManageAmenitiesFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag("FM_MANAGE_AMENITIES", partnerId, "on", {
    user_id: userId,
  });
}

// A membership request has no subscription yet, so accepting it only tells the
// customer the team will finish sign-up with them — nothing is charged. Gates
// the Accept/Reject controls on every order surface for that order kind.
export function useFMMembershipRequestDecisionsFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag("FM_MEMBERSHIP_REQUEST_DECISIONS", partnerId, "on", {
    user_id: userId,
  });
}

export function useFMUserManagementFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag("FM_USER_MANAGEMENT", partnerId, "on", {
    user_id: userId,
  });
}

// Gates the permissions editor on the Team page (the Access column, the
// "Edit permissions" action and the invite form's Permissions section). Chrome
// only: the stored permissions apply whatever the flag says.
export function useFMTeamPermissionsFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag("FM_TEAM_PERMISSIONS", partnerId, "on", {
    user_id: userId,
  });
}

// Gates the "Don't combine promo codes with dynamic pricing" setting on the
// Campaigns page (the card, its dialog of codes, and the per-code tick on the
// campaign form). Chrome only: the partner's switch prices checkout whatever
// the flag says, and checkout explains the outcome without reading it.
export function useFMPromotionsExclusiveOfDynamicPricingFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag(
    "FM_PROMOTIONS_EXCLUSIVE_OF_DYNAMIC_PRICING",
    partnerId,
    "on",
    { user_id: userId }
  );
}

export function useOfferingDailyAccessQuantityFieldFeatureFlag() {
  const treatments = useTreatments([
    "CH_10306_OFFERING_DAILY_ACCESS_QUANTITY_FIELD_FEATURE",
  ]);

  return (
    treatments["CH_10306_OFFERING_DAILY_ACCESS_QUANTITY_FIELD_FEATURE"]
      .treatment === "on"
  );
}

export function useFMMakePaymentDetailsOptional(partnerId: string) {
  const treatments = useTreatments(
    ["CH_10281_FM_MAKE_PAYMENT_DETAILS_OPTIONAL"],
    {
      partner_id: partnerId,
    }
  );

  return (
    treatments["CH_10281_FM_MAKE_PAYMENT_DETAILS_OPTIONAL"].treatment === "on"
  );
}

export function useFMDisplayManageBlockoutDates(partnerId: string) {
  const treatments = useTreatments(["EPIC_10138_MANAGE_BLOCKOUT_DATES"], {
    partner_id: partnerId,
  });

  return treatments["EPIC_10138_MANAGE_BLOCKOUT_DATES"].treatment === "on";
}

export function useFMOrderUpdates(partnerId: string, userId: string) {
  const treatments = useTreatments(["FM_ORDER_UPDATES"], {
    partner_id: partnerId,
    user_id: userId,
  });

  return treatments["FM_ORDER_UPDATES"].treatment === "on";
}

export function useAppleGooglePayEmbedInCheckoutPage(partnerId: string) {
  const treatments = useTreatments(
    ["SC23984_APPLE_GOOGLE_PAY_EMBED_IN_CHECKOUT_PAGE"],
    {
      partner_id: partnerId,
    }
  );

  return (
    treatments["SC23984_APPLE_GOOGLE_PAY_EMBED_IN_CHECKOUT_PAGE"].treatment ===
    "on"
  );
}

export function useCampaignDiscountTypeAmount(partnerId: string) {
  const treatments = useTreatments(
    ["CH20828_CAMPAIGN_CREATE_DISCOUNT_AMOUNT"],
    {
      partner_id: partnerId,
    }
  );

  return (
    treatments["CH20828_CAMPAIGN_CREATE_DISCOUNT_AMOUNT"].treatment === "on"
  );
}

export function useDisplayNavigatorMembershipsFeatureFlag(partnerId: string) {
  const treatments = useTreatments(["DISPLAY_NAVIGATOR_MEMBERSHIPS"], {
    partner_id: partnerId,
  });

  return treatments["DISPLAY_NAVIGATOR_MEMBERSHIPS"].treatment === "on";
}

export function useMembershipPortalFeatureFlag(partnerId: string) {
  const treatments = useTreatments(["CH25850_MEMBERSHIP_PORTAL"], {
    partner_id: partnerId,
  });

  return treatments["CH25850_MEMBERSHIP_PORTAL"].treatment === "on";
}

export function useDisplayMeetingRoomAvailableSpaces(partnerId: string) {
  const treatments = useTreatments(
    ["CH25785_DISPLAY_MEETING_ROOM_AVAILABLE_SPACES"],
    {
      partner_id: partnerId,
    }
  );

  return (
    treatments["CH25785_DISPLAY_MEETING_ROOM_AVAILABLE_SPACES"].treatment ===
    "on"
  );
}

export function useOnboardingPortalFeatureFlag(partnerId: string) {
  const treatments = useTreatments(["CH27256_PORTAL_ONBOARDING"], {
    partner_id: partnerId,
  });

  return treatments["CH27256_PORTAL_ONBOARDING"].treatment === "on";
}

export function useMeetingRoomDailyPricingFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH26976_MEETING_ROOM_DAILY_PRICING", partnerId);
}

export function useApplePayFixFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH28642_APPLE_PAY", partnerId);
}

export function usePartnerOfferingSpacesFeatureFlag() {
  const treatments = useTreatments([
    "CH28662_USE_PARTNER_OFFERING_SPACES_HOOK",
  ]);

  return (
    treatments["CH28662_USE_PARTNER_OFFERING_SPACES_HOOK"].treatment === "on"
  );
}

export function useGalleryEmptyStateUpsell(partnerId: string) {
  return useFeatureFlag("CH29217_GALLERY_EMPTY_UPSELL", partnerId);
}

export function usePortalManageMembership(partnerId: string) {
  return useFeatureFlag("PORTAL_MANAGE_MEMBERSHIP", partnerId);
}

export function useShowMeetingRoomGalleryBanner(partnerId: string) {
  return useFeatureFlag("CH_31283_SHOW_MEETING_ROOM_GALLERY_BANNER", partnerId);
}

export function useHoverPreviewDateRangeFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH30942_HOVER_RREVIEW_DATE_RANGE", partnerId);
}

// A theme flag picks which of two looks customers get rather than gating a
// feature, so the Dashboard's preview must show the look customers actually see.
const THEME_READ: FlagReadOptions = { asPartner: true };

export function useTechArtistaDarkTheme() {
  const treatments = useTreatments(
    ["TECHARTISTA_DARK_THEME"],
    undefined,
    THEME_READ
  );

  return treatments["TECHARTISTA_DARK_THEME"].treatment === "on";
}

export function useEnhanceCheckoutUX() {
  const treatments = useTreatments(["CH31752_ENHANCE_CHECKOUT_UX"]);

  return treatments["CH31752_ENHANCE_CHECKOUT_UX"].treatment === "on";
}

export function useStandartPickerOnMembershipFeatureFlag() {
  const treatments = useTreatments(["CH30908_STANDART_PICKER_ON_MEMBESHIP"]);

  return treatments["CH30908_STANDART_PICKER_ON_MEMBESHIP"].treatment === "on";
}

export function useBillingDetailsUpdate(partnerId: string) {
  return useFeatureFlag("CH31945_BILLING_DETAILS_UPDATE", partnerId);
}

export function useGoogleBookProductionEnvironmentFeatureFlag() {
  const treatments = useTreatments([
    "CH32387_GOOGLE_BOOK_PRODUCTION_ENVIRONMENT",
  ]);

  return (
    treatments["CH32387_GOOGLE_BOOK_PRODUCTION_ENVIRONMENT"].treatment === "on"
  );
}

export function useDisplayMerchantNameAndAddress() {
  const treatments = useTreatments([
    "CH_33395_DISPLAY_MERCHANT_NAME_AND_ADRESS",
  ]);

  return (
    treatments["CH_33395_DISPLAY_MERCHANT_NAME_AND_ADRESS"].treatment === "on"
  );
}

export function useIntelligentOfficePromoFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH_33916_INTELLIGENT_OFFICE_PROMO", partnerId);
}

export function useDisplaySavingAmountFeatureFlag() {
  const treatments = useTreatments(["CH34411_DISPLAY_SAVING_AMOUNT"]);

  return treatments["CH34411_DISPLAY_SAVING_AMOUNT"].treatment === "on";
}

export function useInitializeOneSignalFeatureFlag() {
  const treatments = useTreatments(["CH_34716_INITIALIZE_ONE_SIGNAL"]);

  return treatments["CH_34716_INITIALIZE_ONE_SIGNAL"].treatment === "on";
}

/**
 * Gates the Reports › Insights page (revenue, hours sold and meeting-room
 * utilization charts) and its sidebar entry.
 */
export function useFMReportsInsightsFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag("FM_REPORTS_INSIGHTS", partnerId, "on", {
    user_id: userId,
  });
}

export function useFMReportsAnalyticsFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag("FM_REPORTS_ANALYTICS", partnerId, "on", {
    user_id: userId,
  });
}

/**
 * Whether Insights has replaced the performance report for this partner. The
 * same flag as the Insights page, read as the partner reads it: a superadmin
 * gets Insights everywhere, but must keep the report the partner still uses.
 */
export function useFMPerformanceReportRetiredFeatureFlag(
  partnerId: string,
  userId: string
) {
  const treatments = useTreatments(
    ["FM_REPORTS_INSIGHTS"],
    { partner_id: partnerId, user_id: userId },
    { asPartner: true }
  );
  const { search } = useLocation();

  return (
    readFlagOverride("FM_REPORTS_INSIGHTS", search) ??
    treatments["FM_REPORTS_INSIGHTS"].treatment === "on"
  );
}

export function useFMShowReportsPayoutFeatureFlag(
  partnerId: string,
  userId: string
) {
  const treatments = useTreatments(["FM_SHOW_REPORTS_PAYOUT"], {
    partner_id: partnerId,
    user_id: userId,
  });

  return treatments["FM_SHOW_REPORTS_PAYOUT"].treatment === "on";
}

export function useRefactorPaymentHookFeatureFlag() {
  const treatments = useTreatments(["CH35534_REFACTOR_PAYMENT_HOOK"]);

  return treatments["CH35534_REFACTOR_PAYMENT_HOOK"].treatment === "on";
}

export function useTheSetCollectiveDarkTheme() {
  const treatments = useTreatments(
    ["THE_SET_COLLECTIVE_DARK_THEME"],
    undefined,
    THEME_READ
  );

  return treatments["THE_SET_COLLECTIVE_DARK_THEME"].treatment === "on";
}

export function useGolfSimulationAccessPortalFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH40266_ONE_STATE_ST_PORTAL_MEMBER_GOLF", partnerId);
}

export function useOrdersSummaryFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH44595_ORDERS_SUMMARY", partnerId);
}

export function useHAVNThemeFeatureFlag() {
  const treatments = useTreatments(["WORK_CLUB_HAVN"], undefined, THEME_READ);

  return treatments["WORK_CLUB_HAVN"].treatment === "on";
}

export function usePerformancePageLocationFilterFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH_45498_PERFORMANCE_PAGE_LOCATION_FILTER", partnerId);
}

export function useAddOnCheckoutBoxFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH45862_ADD_ON_CHECKOUT_BOX", partnerId);
}

export function useNine3OnePromoFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH_46371_NINE3_ONE_PROMO", partnerId);
}

export function useDashboardAddRemoveAddOnsFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH45874_DASHBOARD_ADD_REMOVE_ADD_ONS", partnerId);
}

export function useUnionHallMeetingRoomsPortalFeatureFlag(
  partnerId: string,
  userId: string,
  membershipPlanId: string
) {
  return useFeatureFlag("UNION_HALL_PORTAL_MEETING_ROOMS", partnerId, "on", {
    user_id: userId,
    membership_plan_id: membershipPlanId,
  });
}

export function useEveDataAgentFeatureFlag(partnerId: string, userId: string) {
  const treatments = useTreatments(["FM_EVE_DATA_AGENT_ORDER_INSIGHTS"], {
    partner_id: partnerId,
    user_id: userId,
  });

  return treatments["FM_EVE_DATA_AGENT_ORDER_INSIGHTS"].treatment === "on";
}

export function useFMCampaignLabelsFlag(partnerId: string, userId: string) {
  return useFeatureFlag("FM_CAMPAIGN_LABELS", partnerId, "on", {
    user_id: userId,
  });
}

export function useFMMembershipPricingManagementFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag("FM_MEMBERSHIP_PRICING_MANAGEMENT", partnerId, "on", {
    user_id: userId,
  });
}

export function useShowDailyRateAccessMessageFeatureFlag(partnerId: string) {
  return useFeatureFlag("SHOW_DAILY_RATE_ACCESS_MESSAGE", partnerId);
}

export function useLayoutCustomizationFeatureFlag(
  partnerId: string,
  userId: string
) {
  return useFeatureFlag("CH_49167_LAYOUT_CUSTOMIZATION", partnerId, "on", {
    user_id: userId,
  });
}

// Gates the customer's own manage-booking page — the single-booking view, its
// change-date/time flow, and the confirmation page's cancel moving there. The
// surfaces reading it today have no signed-in reader — a customer holding a
// signed link — so `user_id` is sent only when a surface has one, and never as a
// placeholder that Split rules would match on. The backend checks the same key
// on the token branch, so turning it off closes the page and the endpoints it
// drives together.
export function useCustomerManageOrderFeatureFlag(
  partnerId: string,
  userId?: string
) {
  return useFeatureFlag(
    "CUSTOMER_MANAGE_ORDER",
    partnerId,
    "on",
    userId ? { user_id: userId } : undefined
  );
}
